import {
  useState, useRef, useEffect, useCallback,
} from 'react';
import axios, { AxiosRequestConfig, ResponseType } from 'axios';
import log from '../utils/logger';

/**
 * Configuration interface for the SSE stream.
 */
interface SSEConfig<T, F> {
  /** The URL of the SSE stream endpoint (should start with '/') */
  source: string;
  /** Additional headers to be included in the SSE request */
  headers?: Record<string, string>;
  /** HTTP method to use for the request (GET or POST) */
  method?: 'GET' | 'POST';
  /** Callback function to handle regular streaming messages */
  onMessage: (data: T) => void;
  /**
   * Callback function for server close event with final data.
   * The server signals completion with a valid flag, optional data payload, and optional error/message.
   * F is the shape of the `data` field; the envelope also carries `error` and `message`.
   */
  onServerClose?: (finalMessage: { valid: boolean; data: F; error?: string; message?: string }) => void;
  /** Optional callback function to handle any errors that occur during streaming */
  onError?: (error: Error) => void;
}

/**
 * Internal state interface for the SSE stream.
 * This state is managed automatically by the hook and exposed to consumers.
 */
interface SSEState {
  /** Whether the stream is currently active */
  isStreaming: boolean;
  /** Any error that occurred during streaming */
  error: Error | null;
}

/**
 * Default headers required for SSE connections.
 * Note: Some headers like Connection are managed by the browser and cannot be set manually.
 */
const DEFAULT_SSE_HEADERS = {
  Accept: 'text/event-stream',
  'Cache-Control': 'no-cache',
} as const;

const SSE_FRAME_SEPARATOR = '\n\n\n\n';

/** True when an SSE frame is comment-only (lines starting with `:`). */
export const isSseCommentOnlyMessage = (message: string): boolean => {
  const lines = message.split('\n').filter((line) => line.trim().length > 0);
  return lines.length > 0 && lines.every((line) => line.startsWith(':'));
};

/** Drop leading SSE comment lines so a keepalive prefix does not merge with the next event. */
export const stripLeadingSseComments = (frame: string): string => {
  const lines = frame.split('\n');
  let start = 0;
  while (start < lines.length) {
    const trimmed = lines[start].trim();
    const isCommentOrBlank = trimmed === '' || lines[start].startsWith(':');
    if (!isCommentOrBlank) {
      break;
    }
    start += 1;
  }
  return lines.slice(start).join('\n');
};

const dispatchSseFrame = <T, F>(
  frame: string,
  config: SSEConfig<T, F>,
): void => {
  const payload = stripLeadingSseComments(frame);
  if (!payload.trim()) return;
  if (isSseCommentOnlyMessage(payload)) return;

  const event = payload.match(/event:\s*(\w+)/)?.[1];
  const data = payload.match(/data:\s*(.+)$/m)?.[1];

  if (!event || !data) return;

  try {
    const parsedData = JSON.parse(data);
    if (event === 'message') {
      config.onMessage?.(parsedData);
    } else if (event === 'server_close') {
      config.onServerClose?.(parsedData);
    }
  } catch (error) {
    config.onError?.(new Error('Failed to parse stream data'));
    log.info('error in processChunk', error, 'chunk', event, data);
  }
};

/**
 * Parse only newly complete SSE frames from cumulative `responseText`
 * (axios `onDownloadProgress` replays the full buffer each time).
 */
export const consumeNewSseFrames = <T, F>(
  fullText: string,
  consumedLength: number,
  config: SSEConfig<T, F>,
): number => {
  // axios cumulative responseText only grows; a shorter buffer means a new stream.
  let cursor = fullText.length < consumedLength ? 0 : consumedLength;

  while (cursor < fullText.length) {
    const sepIdx = fullText.indexOf(SSE_FRAME_SEPARATOR, cursor);
    if (sepIdx === -1) break;

    const rawFrame = fullText.slice(cursor, sepIdx);
    cursor = sepIdx + SSE_FRAME_SEPARATOR.length;
    dispatchSseFrame(rawFrame, config);
  }

  return cursor;
};

/** True for axios abort/cancel errors that must not surface as stream failures. */
export const isIntentionalStreamCancellation = (error: unknown): boolean => {
  if (axios.isCancel(error)) {
    return true;
  }
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }
  if (error instanceof Error && error.name === 'AbortError') {
    return true;
  }
  if (
    typeof error === 'object'
    && error !== null
    && 'code' in error
    && (error as { code?: string }).code === 'ERR_CANCELED'
  ) {
    return true;
  }
  return false;
};

/**
 * A React hook for handling Server-Sent Events (SSE) streams.
 *
 * This hook provides a way to establish and manage SSE connections with automatic
 * cleanup and error handling. It supports two types of events:
 *
 * 1. Regular messages (event: message):
 *    - Contains raw payload data
 *    - Processed via onMessage callback
 *
 * 2. Server close messages (event: server_close):
 *    - Contains {valid: boolean, data: T} structure
 *    - Signals stream completion with final data
 *    - Processed via onServerClose callback
 *
 * @example
 * ```typescript
 * // GET request example
 * const { startStream, stopStream, isStreaming } = useSSEStream<EvalData, FinalEvalData>({
 *   source: '/api/stream',
 *   onMessage: (data) => console.log('Stream update:', data),
 *   onServerClose: (final) => console.log('Final data:', final.data),
 *   onError: (error) => console.error(error)
 * });
 *
 * // POST request example
 * const { startStream, stopStream, isStreaming } = useSSEStream<EvalData, FinalEvalData>({
 *   source: '/api/stream',
 *   method: 'POST',
 *   body: { key: 'value' },
 *   onMessage: (data) => console.log('Stream update:', data),
 *   onServerClose: (final) => console.log('Final data:', final.data),
 *   onError: (error) => console.error(error)
 * });
 * ```
 *
 * @param config - Configuration object for the SSE stream
 * @returns Object containing stream control functions and state
 */
export const useSSEStream = <T, F>(config: SSEConfig<T, F>) => {
  // Internal state for stream status and errors
  const [state, setState] = useState<SSEState>({
    isStreaming: false,
    error: null,
  });

  // Reference to the AbortController for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);
  /** Monotonic id so late rejections from superseded requests cannot mutate state. */
  const activeRequestIdRef = useRef(0);
  /** Bytes of cumulative responseText already parsed into complete frames. */
  const consumedResponseLengthRef = useRef(0);

  const processResponseText = useCallback(
    (fullText: string) => {
      consumedResponseLengthRef.current = consumeNewSseFrames(
        fullText,
        consumedResponseLengthRef.current,
        config,
      );
    },
    [config],
  );

  /**
   * Start the SSE stream. This function will:
   * 1. Clean up any existing stream
   * 2. Initialize a new stream
   * 3. Begin processing messages
   *
   * @param overrideUrl - Optional URL to override the configured source URL
   */
  const startStream = useCallback(
    async (overrideUrl?: string, body?: unknown) => {
      // Don't start if no source URL is provided
      const url = overrideUrl || config.source;

      if (!url) return;

      abortControllerRef.current?.abort();
      activeRequestIdRef.current += 1;
      const requestId = activeRequestIdRef.current;
      setState({ isStreaming: true, error: null });
      abortControllerRef.current = new AbortController();
      consumedResponseLengthRef.current = 0;

      try {
        // Remove any leading slashes and ensure single leading slash
        const normalizedUrl = url.replace(/^\/+/, '');
        const finalUrl = normalizedUrl ? `/${normalizedUrl}` : '';

        // Common request config
        const requestConfig: AxiosRequestConfig = {
          headers: {
            ...DEFAULT_SSE_HEADERS,
            ...config.headers,
          },
          signal: abortControllerRef.current.signal,
          responseType: 'text' as ResponseType,
          onDownloadProgress: (progressEvent) => {
            const data = progressEvent.event.target.responseText;
            processResponseText(data);
          },
        };

        // Use POST or GET based on config
        if (config.method === 'POST') {
          await axios.post(finalUrl, body, requestConfig);
        } else {
          await axios.get(finalUrl, requestConfig);
        }

        if (requestId !== activeRequestIdRef.current) {
          return;
        }

        setState({ isStreaming: false, error: null });
      } catch (error) {
        if (requestId !== activeRequestIdRef.current) {
          return;
        }
        if (isIntentionalStreamCancellation(error)) {
          return;
        }
        const streamError = error instanceof Error ? error : new Error(String(error));
        setState({ isStreaming: false, error: streamError });
        config.onError?.(streamError);
      }
    },
    [config, processResponseText],
  );

  /**
   * Stop the SSE stream and clean up resources.
   */
  const stopStream = useCallback(() => {
    abortControllerRef.current?.abort();
    activeRequestIdRef.current += 1;
    setState({ isStreaming: false, error: null });
  }, []);

  // Cleanup on unmount or when config changes
  useEffect(() => () => {
    abortControllerRef.current?.abort();
  }, []);

  return {
    ...state,
    startStream,
    stopStream,
  };
};
